import { useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import { notificationActionType } from "@/react/components/Notifications/NotificationsList/List/Actions/ActionContent";
import { Button } from "@/react/components/shared/uikit/Button";
import { invalidateNotificationsQueryCache } from "@/react/components/shared/uikit/HeaderV3/RightActionBlock/Notifications/useHeaderNotificationsApi";
import { useToast } from "@/react/components/shared/uikit/ToastV2";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useAcceptConnection } from "@/react/hooks/connections/useAcceptConnection";
import { useIgnoreConnection } from "@/react/hooks/connections/useIgnoreConnection";
import type { Notification } from "@/react/types/Notification";
import { GroupUserImage } from "../GroupUserImage";
import { Item } from "../Item";
import { Actions } from "../Item/Actions";
import { TimeStamp } from "../TimeStamp";
import { UserImageWithIcon } from "../UserImageWithIcon";

const i18nBase = "member_profile_modal.notifications";

export const ConnectRequestNotification = ({
  notification,
}: {
  notification: Notification;
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { mutate: acceptConnection, isLoading: isAccepting } =
    useAcceptConnection({
      onSuccess: () => {
        toast.success(
          t("profile_drawer.connect_banner.messages.connection_accepted", {
            memberName: notification.actor_name,
          }),
        );
        invalidateNotificationsQueryCache({ queryClient });
      },
      onError: () => {
        toast.error(
          t("profile_drawer.connect_banner.messages.connection_accept_error"),
        );
      },
    });

  const { mutate: ignoreConnection, isLoading: isIgnoring } =
    useIgnoreConnection({
      onSuccess: () => {
        toast.success(
          t("profile_drawer.connect_banner.messages.connection_ignored"),
        );
        invalidateNotificationsQueryCache({ queryClient });
      },
      onError: () => {
        toast.error(
          t("profile_drawer.connect_banner.messages.connection_ignore_error"),
        );
      },
    });

  const handleAcceptConnection = () => {
    acceptConnection(notification.notifiable_id.toString());
  };

  const handleIgnoreConnection = () => {
    ignoreConnection(notification.notifiable_id.toString());
  };

  return (
    <div
      className={classNames(
        "flex w-full items-center",
        notificationActionType(notification),
      )}
    >
      {notification.second_actor_name ? (
        <GroupUserImage notification={notification} />
      ) : (
        <UserImageWithIcon
          src={notification.actor_image || ""}
          name={notification.actor_name}
          action={notification.action}
          size="9"
        />
      )}
      <div
        className="flex w-full flex-col items-start space-y-1"
        data-testid="notification-details"
      >
        <Item notification={notification} />
        <TimeStamp createdAt={notification.created_at} />
        <div className="flex gap-2">
          <Button
            variant="secondary"
            type="button"
            onClick={handleIgnoreConnection}
            disabled={isIgnoring || isAccepting}
          >
            {t(`${i18nBase}.ignore`)}
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={handleAcceptConnection}
            disabled={isAccepting || isIgnoring}
          >
            {t(`${i18nBase}.accept`)}
          </Button>
        </div>
      </div>

      <div>
        {!notification.read_at ? (
          <div className="flex h-6 w-6 items-center justify-center group-hover:hidden">
            <span className="bg-messaging-info-blue inline-block h-2 w-2 rounded-full group-hover:hidden" />
          </div>
        ) : (
          <div className="flex h-6 w-6 group-hover:hidden" />
        )}
        <Actions notification={notification} />
      </div>
    </div>
  );
};
